import { FC, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { Feature } from '../../services/backstage/Feature';
import { ClockInStatus, useClockService } from '../../services/clockIn/ClockService';
import { WorkTimeDataSource } from '../../services/gql/graphql.generated';
import { useLocationService } from '../../services/location/LocationService';
import { SelectInput, SelectInputOption } from '../form/SelectForm';
import { Button } from '../shared/button/Button';
import { Divider } from '../shared/Divider';
import { Watch } from '../shared/icons/Watch';
import strings from './clock.translations';

interface ClockManuallyProps {
  type: ClockInStatus;
  onSuccess: () => void;
  onForgotten: () => void;
}

export const ClockManually: FC<ClockManuallyProps> = ({ type, onSuccess, onForgotten }) => {
  const clockState = useClockService(service => service.clockState);
  const clockLocationId = useClockService(service => service.clockLocationId);
  const setClockState = useClockService(service => service.setClockState);
  const loading = useClockService(service => service.loadingState);
  const error = useClockService(service => service.errorMessage);

  const handleError = useErrorHandler();
  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);
  const setSelectedLocation = useLocationService(state => state.setSelectedLocation);
  const locationList = useLocationService(state => state.locationList);

  const locationId = clockLocationId || selectedLocationId;

  const locationOptions = locationList?.map(loc => ({
    id: loc.id.toString(),
    name: loc.name
  }));

  const getDefaultOption = () => {
    return locationOptions.find(opt => opt.id === locationId?.toString());
  };

  const [selectedOption, setSelectedOption] = useState<SelectInputOption | undefined>(getDefaultOption);

  const onLocationChange = (newLocOption?: SelectInputOption) => {
    setSelectedOption(newLocOption);
    if (newLocOption) {
      const newLoc = locationList.find(loc => loc.id.toString() === newLocOption.id);

      if (newLoc) {
        setSelectedLocation(newLoc);
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (locationId === undefined) return;
      await setClockState(type, locationId, WorkTimeDataSource.Manual);
      onSuccess();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <>
      <Feature name="locationClockIn">
        <p className="text-sm font-content leading-5">{strings.manualClockInWarning}</p>
      </Feature>
      {locationOptions.length > 1 && (
        <SelectInput
          name="selected-location"
          placeholder={strings.selectLocation}
          value={selectedOption}
          onChange={onLocationChange}
          options={locationOptions}
          className="w-72"
          disabled={!!clockLocationId}
        />
      )}
      {error && <p className="text-sm text-red-500 leading-5">{error}</p>}
      <Button
        primary
        loading={loading}
        onClick={() => {
          onSubmit();
        }}
        className="flex items-center justify-center w-full gap-2"
      >
        <Watch className="w-4 h-6" />
        {clockState === ClockInStatus.In ? strings.common.clockOut : strings.common.clockIn}
      </Button>
      <Feature name="retroClockIn">
        <>
          <Divider />
          <Button
            className="mt-4"
            onClick={() => {
              onForgotten();
            }}
          >
            {strings.forgotClockingTimeWarning(type)}
          </Button>
        </>
      </Feature>
    </>
  );
};
